/* =========================================================================
   Numi CRO test scripts — scoped, additive, idempotent.

   One guard, one initAll(), every module marks the elements it touches with
   data-numi-init so a second include, a re-run, or a shopify:section:load in
   the theme editor never double-binds. Safe to load once globally in
   layout/theme.liquid; the sections also include it inline as a fallback.

   Modules: gallery, accordion, carousels, collection UI (filter + sort),
   lazy video posters + tiles, savings calculator, PDP buy box (variant ->
   cart, reuses the theme's own cart drawer), Fit Finder quiz (weighted
   scoring, override/precedence forces, two results, optional mesh pairing).
   ========================================================================= */
(function () {
  "use strict";
  if (window.__numiCroInit) return;
  window.__numiCroInit = true;

  function once(el) {
    if (!el || el.getAttribute("data-numi-init") === "1") return false;
    el.setAttribute("data-numi-init", "1");
    return true;
  }
  function money(n) { return "$" + Math.round(n).toLocaleString(); }
  function parseJSONAttr(el, attr) {
    var raw = el.getAttribute(attr);
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (e) { return null; }
  }

  /* ---------- Gallery: click thumbs to swap hero ---------- */
  function initGallery(scope) {
    scope.querySelectorAll("[data-numi-gallery]").forEach(function (root) {
      if (!once(root)) return;
      var hero = root.querySelector("[data-numi-gallery-hero] img");
      var thumbs = root.querySelectorAll("[data-numi-gallery-thumb]");
      thumbs.forEach(function (btn) {
        btn.addEventListener("click", function () {
          var src = btn.getAttribute("data-src");
          if (hero && src) { hero.src = src; hero.alt = btn.getAttribute("data-alt") || ""; }
          thumbs.forEach(function (b) { b.classList.remove("is-active"); });
          btn.classList.add("is-active");
        });
      });
    });
  }

  /* ---------- Accordion: single-open toggle + aria-expanded ---------- */
  function initAccordion(scope) {
    scope.querySelectorAll("[data-numi-accordion]").forEach(function (root) {
      if (!once(root)) return;
      var rows = root.querySelectorAll(".numi-test-accordion__row");
      rows.forEach(function (row) {
        var head = row.querySelector(".numi-test-accordion__head");
        if (!head) return;
        head.addEventListener("click", function () {
          var open = row.classList.contains("is-open");
          rows.forEach(function (r) {
            r.classList.remove("is-open");
            var h = r.querySelector(".numi-test-accordion__head");
            if (h) h.setAttribute("aria-expanded", "false");
          });
          if (!open) { row.classList.add("is-open"); head.setAttribute("aria-expanded", "true"); }
        });
      });
    });
  }

  /* ---------- Carousel ---------- */
  function initCarousels(scope) {
    scope.querySelectorAll("[data-numi-carousel]").forEach(function (root) {
      if (!once(root)) return;
      var track = root.querySelector("[data-numi-carousel-track]");
      var prev = root.querySelector("[data-numi-carousel-prev]");
      var next = root.querySelector("[data-numi-carousel-next]");
      if (!track) return;
      function step() {
        var first = track.children[0];
        if (!first) return track.clientWidth;
        var style = window.getComputedStyle(track);
        var gap = parseFloat(style.columnGap || style.gap || "0") || 0;
        return first.getBoundingClientRect().width + gap;
      }
      function sync() {
        var max = track.scrollWidth - track.clientWidth - 1;
        if (prev) prev.disabled = track.scrollLeft <= 1;
        if (next) next.disabled = track.scrollLeft >= max;
      }
      if (prev) prev.addEventListener("click", function () { track.scrollBy({ left: -step(), behavior: "smooth" }); });
      if (next) next.addEventListener("click", function () { track.scrollBy({ left: step(), behavior: "smooth" }); });
      track.addEventListener("scroll", sync, { passive: true });
      if ("ResizeObserver" in window) new ResizeObserver(sync).observe(track);
      sync();
    });
  }

  /* ---------- Collection: mobile filter toggle + sort ---------- */
  function initCollectionUI(scope) {
    scope.querySelectorAll("[data-numi-filter-toggle]").forEach(function (btn) {
      if (!once(btn)) return;
      var panel = document.getElementById(btn.getAttribute("aria-controls") || "");
      if (!panel) return;
      btn.addEventListener("click", function () {
        var open = panel.hasAttribute("hidden");
        if (open) panel.removeAttribute("hidden"); else panel.setAttribute("hidden", "");
        btn.setAttribute("aria-expanded", open ? "true" : "false");
      });
    });
    scope.querySelectorAll("[data-numi-sort]").forEach(function (sel) {
      if (!once(sel)) return;
      sel.addEventListener("change", function () {
        var url = new URL(window.location.href);
        url.searchParams.set("sort_by", sel.value);
        url.searchParams.delete("page");
        window.location.href = url.toString();
      });
    });
  }

  /* ---------- Lazy video posters + click-to-play tiles ---------- */
  var vObserver = ("IntersectionObserver" in window) ? new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      var el = entry.target, src = el.getAttribute("data-poster");
      if (src && el.tagName === "IMG") el.src = src;
      vObserver.unobserve(el);
    });
  }, { rootMargin: "200px" }) : null;

  function initVideo(scope) {
    scope.querySelectorAll("[data-numi-lazy-poster]").forEach(function (img) {
      if (!once(img)) return;
      if (vObserver) vObserver.observe(img); else img.src = img.getAttribute("data-poster");
    });
    scope.querySelectorAll("[data-numi-video-tile]").forEach(function (tile) {
      if (!once(tile)) return;
      tile.addEventListener("click", function () {
        var videoUrl = tile.getAttribute("data-video");
        if (!videoUrl) return;
        var modal = document.createElement("div");
        modal.className = "numi-test-modal is-open";
        modal.innerHTML =
          '<div class="numi-test-modal__scrim" data-close></div>' +
          '<div class="numi-test-modal__inner">' +
          '<video src="' + videoUrl + '" controls autoplay playsinline style="width:100%;height:100%;object-fit:contain;"></video>' +
          '<button data-close class="numi-test-modal__close" aria-label="Close">Close</button>' +
          "</div>";
        document.body.appendChild(modal);
        modal.querySelectorAll("[data-close]").forEach(function (el) {
          el.addEventListener("click", function () { modal.remove(); });
        });
      });
    });
  }

  /* ---------- Savings calculator (block-driven, fully editable) ----------
     Each "path" button carries its question/note; each slider carries whether
     its value is money and its annual multiplier. The optional "all" tab sums
     every slider. All copy and coefficients come from the section, not here.   */
  function initSavings(scope) {
    scope.querySelectorAll("[data-numi-savings]").forEach(function (root) {
      if (!once(root)) return;
      var PRICE = parseFloat(root.getAttribute("data-price") || "65") || 0;
      var labels = parseJSONAttr(root, "data-labels") || {};
      var inputs = {}, meta = {};
      root.querySelectorAll("[data-numi-input]").forEach(function (el) {
        var k = el.getAttribute("data-numi-input");
        inputs[k] = el;
        meta[k] = { money: el.getAttribute("data-money") === "1", mult: parseFloat(el.getAttribute("data-multiplier") || "0") || 0 };
        el.addEventListener("input", render);
      });
      var pathBtns = Array.prototype.slice.call(root.querySelectorAll("[data-numi-savings-path]"));
      var paths = {};
      pathBtns.forEach(function (b) {
        var key = b.getAttribute("data-numi-savings-path");
        paths[key] = {
          question: b.getAttribute("data-question") || "",
          note: b.getAttribute("data-note") || "",
          fields: key === "all" ? Object.keys(inputs) : [key]
        };
      });
      var current = pathBtns.length ? pathBtns[0].getAttribute("data-numi-savings-path") : null;
      function val(k) { return inputs[k] ? (parseFloat(inputs[k].value) || 0) : 0; }
      function outText(k) { return (meta[k] && meta[k].money) ? money(val(k)) : String(val(k)); }
      function render() {
        var cfg = paths[current]; if (!cfg) return;
        root.querySelectorAll("[data-numi-field]").forEach(function (f) {
          f.hidden = cfg.fields.indexOf(f.getAttribute("data-numi-field")) === -1;
        });
        Object.keys(inputs).forEach(function (k) {
          var el = root.querySelector('[data-numi-out="' + k + '"]'); if (el) el.textContent = outText(k);
        });
        var saved = 0;
        cfg.fields.forEach(function (k) { saved += val(k) * (meta[k] ? meta[k].mult : 0); });
        saved = Math.round(saved);
        var net = saved - PRICE;
        var set = function (sel, txt) { var e = root.querySelector(sel); if (e) e.textContent = txt; };
        set("[data-numi-savings-question]", cfg.question);
        set("[data-numi-savings-note]", cfg.note);
        set("[data-numi-savings-total]", money(saved));
        set("[data-numi-savings-saved]", money(saved));
        set("[data-numi-savings-against]", "Against " + money(PRICE) + (labels.against ? " " + labels.against : ""));
        set("[data-numi-savings-netlabel]", net >= 0 ? (labels.ahead || "Ahead by") : (labels.short || "Short by"));
        set("[data-numi-savings-net]", money(Math.abs(net)));
      }
      pathBtns.forEach(function (btn) {
        btn.addEventListener("click", function () {
          current = btn.getAttribute("data-numi-savings-path");
          pathBtns.forEach(function (b) { b.classList.remove("is-active"); });
          btn.classList.add("is-active");
          render();
        });
      });
      render();
    });
  }

  /* ---------- PDP buy box: variant selection + add via theme cart ----------
     Reuses the theme's own AJAX cart drawer (does NOT rebuild the cart):
     after /cart/add.js it dispatches cart:refresh and clicks the theme's
     cart toggle to open the populated drawer, falling back to /cart.        */
  function initBuybox(scope) {
    scope.querySelectorAll("[data-numi-buybox]").forEach(function (root) {
      if (!once(root)) return;
      // Variant data lives in the <script type="application/json" data-numi-variants>
      // element's TEXT content, not an attribute — read it from there.
      var variantsEl = root.querySelector("[data-numi-variants]");
      var variants = [];
      if (variantsEl) {
        try { variants = JSON.parse(variantsEl.textContent) || []; } catch (e) { variants = []; }
      }
      var idInput = root.querySelector('input[name="id"]');
      var priceEls = root.querySelectorAll("[data-numi-price]");
      var addBtn = root.querySelector("[data-numi-add]");
      var addLabel = root.querySelector("[data-numi-add-label]");
      // Gallery hero + thumbs, so selecting a color shows that color's image.
      var galleryHero = root.querySelector("[data-numi-gallery-hero] img");
      var galleryThumbs = root.querySelectorAll("[data-numi-gallery-thumb]");
      var form = root.querySelector("form[action*='/cart/add'], form[data-numi-form]");
      var groups = Array.prototype.slice.call(root.querySelectorAll("[data-numi-option-position]"));

      function optsOf(v) {
        return v.options || [v.option1, v.option2, v.option3].filter(function (x) { return x != null; });
      }
      function selectedValues() {
        var vals = [];
        groups.forEach(function (g) {
          var pos = parseInt(g.getAttribute("data-numi-option-position"), 10);
          var checked = g.querySelector("input:checked, [aria-checked='true'][data-value]");
          vals[pos - 1] = checked ? (checked.value || checked.getAttribute("data-value")) : null;
        });
        return vals;
      }
      function match(vals) {
        return variants.filter(Boolean).find(function (v) {
          var opts = optsOf(v);
          return opts.length === vals.length && vals.every(function (val, i) { return val != null && opts[i] === val; });
        });
      }
      // Nearest valid variant when a combination doesn't exist: keep the option the
      // shopper just changed, prefer an available variant, so price never dead-ends.
      function nearestFor(pos, value) {
        var pool = variants.filter(Boolean).filter(function (v) { return optsOf(v)[pos - 1] === value; });
        return pool.find(function (v) { return v.available; }) || pool[0] || null;
      }
      function checkRadiosFor(v) {
        var opts = optsOf(v);
        groups.forEach(function (g) {
          var pos = parseInt(g.getAttribute("data-numi-option-position"), 10);
          var want = opts[pos - 1];
          g.querySelectorAll("input[type=radio]").forEach(function (input) {
            if (input.value === want && !input.checked) { input.checked = true; }
          });
        });
      }
      function fmtMoney(cents) {
        if (window.Shopify && Shopify.formatMoney && window.theme && theme.moneyFormat) {
          return Shopify.formatMoney(cents, theme.moneyFormat);
        }
        return "$" + (cents / 100).toFixed(2).replace(/\.00$/, "");
      }
      function applyVariant(v) {
        if (idInput) idInput.value = v.id;
        // Show the selected variant's image when it has one. No-ops for products
        // whose variants share a single image, so it is safe for every product.
        if (galleryHero && v.featured_image && v.featured_image.src) {
          var vsrc = v.featured_image.src;
          galleryHero.src = vsrc;
          galleryHero.alt = v.featured_image.alt || galleryHero.alt || "";
          if (galleryThumbs && galleryThumbs.length) {
            var vkey = vsrc.split("?")[0].split("/").pop();
            galleryThumbs.forEach(function (t) {
              var tsrc = t.getAttribute("data-src") || "";
              var on = tsrc.split("?")[0].split("/").pop() === vkey;
              t.classList.toggle("is-active", on);
            });
          }
        }
        var priced = fmtMoney(v.price);
        priceEls.forEach(function (el) { el.textContent = priced; });
        if (addBtn) {
          addBtn.disabled = !v.available;
          if (addLabel) addLabel.textContent = v.available ? ("Add to bag · " + priced) : "Sold out";
        }
        var colorLabel = root.querySelector("[data-numi-color-label]");
        groups.forEach(function (g) {
          var oname = (g.getAttribute("data-numi-option-name") || "").toLowerCase();
          if (oname.indexOf("color") === 0 || oname.indexOf("colour") === 0) {
            var checked = g.querySelector("input:checked");
            if (colorLabel && checked) colorLabel.textContent = checked.value;
          }
        });
      }
      function update(changedPos) {
        var v = match(selectedValues());
        if (!v && changedPos != null) {
          v = nearestFor(changedPos, selectedValues()[changedPos - 1]);
          if (v) checkRadiosFor(v);
        }
        if (!v) { if (addBtn) { addBtn.disabled = true; if (addLabel) addLabel.textContent = "Unavailable"; } return; }
        applyVariant(v);
      }
      groups.forEach(function (g) {
        var pos = parseInt(g.getAttribute("data-numi-option-position"), 10);
        g.addEventListener("change", function () { update(pos); });
        g.querySelectorAll("[data-value]").forEach(function (b) {
          if (b.tagName === "BUTTON") b.addEventListener("click", function () {
            g.querySelectorAll("[data-value]").forEach(function (x) { x.setAttribute("aria-checked", "false"); x.classList.remove("is-active"); });
            b.setAttribute("aria-checked", "true"); b.classList.add("is-active"); update(pos);
          });
        });
      });
      update();

      if (form) {
        form.addEventListener("submit", function (e) {
          if (!window.fetch) return; // let native submit handle it
          e.preventDefault();
          var id = idInput ? idInput.value : null;
          if (!id) return;
          if (addBtn) addBtn.disabled = true;
          fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", "Accept": "application/json" },
            body: JSON.stringify({ id: id, quantity: 1 })
          }).then(function (r) { return r.json(); }).then(function () {
            // Reformation refreshes the cart-drawer UI off cart:refresh dispatched
            // on documentElement with bubbles (see theme JS-events docs). It does
            // NOT open the drawer; open it via the header toggle (#cart-drawer-toggle).
            document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
            var toggle = document.getElementById("cart-drawer-toggle");
            if (toggle) { toggle.click(); }
            else { window.location.href = "/cart"; }
            if (addBtn) addBtn.disabled = false;
          }).catch(function () { if (form) form.submit(); });
        });
      }
    });
  }

  /* ---------- Fit Finder quiz ----------
     Weighted scoring with override/precedence "forces" and up to two results,
     plus an optional modal+mesh pairing. All copy and weights come from the
     section blocks so the sheet stays the source of truth.                    */
  function initQuiz(scope) {
    scope.querySelectorAll("[data-numi-quiz]").forEach(function (quiz) {
      if (!once(quiz)) return;
      var steps = Array.prototype.slice.call(quiz.querySelectorAll("[data-quiz-step]"));
      var resultWrap = quiz.querySelector("[data-quiz-result]");
      var backBtn = quiz.querySelector("[data-quiz-back]");
      var countEl = quiz.querySelector("[data-quiz-count]");
      var fillEl = quiz.querySelector("[data-quiz-fill]");
      var total = steps.length;
      var idx = 0;
      var answers = {}; // stepId -> chosen option element
      var offered = (quiz.getAttribute("data-offered") || "").split(",").map(function (s) { return s.trim(); }).filter(Boolean);
      var meshMap = parseJSONAttr(quiz, "data-mesh-map") || {};
      var showTwo = quiz.getAttribute("data-second") !== "0";
      var fallback = quiz.getAttribute("data-default-handle") || offered[0];
      var quizPicks = [], quizDetails = [];

      // Height-based flagship tie-break: the height options carry data-tiebreak
      // (tall -> Signature, short -> Crop). Last one answered wins.
      function computeTiebreak() {
        var tb = null;
        Object.keys(answers).forEach(function (sid) {
          var v = answers[sid] && answers[sid].getAttribute("data-tiebreak");
          if (v) tb = v;
        });
        return tb;
      }
      // Answers as {question, answer} for instrumentation (read from the DOM so
      // it always reflects the merchant's edited copy).
      function answerDetails() {
        return steps.map(function (step) {
          var opt = answers[step.getAttribute("data-quiz-step-id")];
          var q = step.querySelector(".numi-test-quiz__q");
          var lbl = opt && opt.querySelector(".numi-test-quiz__opt-label");
          return { question: q ? q.textContent.trim() : "", answer: (opt && lbl) ? lbl.textContent.trim() : "" };
        }).filter(function (a) { return a.answer; });
      }

      function show(i) {
        steps.forEach(function (s, k) { s.style.display = k === i ? "block" : "none"; });
        if (resultWrap) resultWrap.style.display = "none";
        if (countEl) countEl.textContent = (i + 1) + " / " + total;
        if (fillEl) fillEl.style.width = ((i / total) * 100) + "%";
        if (backBtn) backBtn.toggleAttribute("disabled", i === 0);
      }

      function scoreAndForce() {
        var totals = {}, forces = [], meshWanted = false;
        offered.forEach(function (h) { totals[h] = 0; });
        Object.keys(answers).forEach(function (stepId) {
          var opt = answers[stepId];
          if (!opt) return;
          var s = parseJSONAttr(opt, "data-scores") || {};
          Object.keys(s).forEach(function (k) { if (k in totals) totals[k] += Number(s[k]) || 0; });
          var force = (opt.getAttribute("data-force") || "").split(",").map(function (x) { return x.trim(); }).filter(Boolean);
          if (force.length) forces.push(force);
          if (opt.getAttribute("data-mesh") === "1") meshWanted = true;
        });
        // Precedence: intersect all force sets; if the intersection is empty, keep the last force set.
        var allowed = null;
        forces.forEach(function (set) {
          if (allowed === null) { allowed = set.slice(); return; }
          var next = allowed.filter(function (h) { return set.indexOf(h) !== -1; });
          allowed = next.length ? next : set.slice();
        });
        return { totals: totals, allowed: allowed, meshWanted: meshWanted };
      }

      function rank() {
        var r = scoreAndForce();
        var tb = computeTiebreak();
        var pool = (r.allowed && r.allowed.length) ? r.allowed.filter(function (h) { return offered.indexOf(h) !== -1; }) : offered.slice();
        // Sort by score desc, tie-break by offered order (stable).
        var order = {};
        offered.forEach(function (h, i) { order[h] = i; });
        pool.sort(function (a, b) { return (r.totals[b] || 0) - (r.totals[a] || 0) || order[a] - order[b]; });
        // Flagship tie-break: with no decisive leader, prefer the height-based
        // flagship (Signature tall / Crop short) when it is still eligible.
        if (tb && pool.indexOf(tb) !== -1) {
          var topScore = r.totals[pool[0]] || 0;
          if (topScore === 0 || (r.totals[tb] || 0) === topScore) {
            pool = pool.filter(function (h) { return h !== tb; });
            pool.unshift(tb);
          }
        }
        var picks = [];
        if (pool.length) picks.push(pool[0]);
        if (showTwo && picks.length) {
          // Prefer the mesh sibling of the top pick when the shopper runs hot.
          if (r.meshWanted && meshMap[pool[0]] && offered.indexOf(meshMap[pool[0]]) !== -1) {
            picks.push(meshMap[pool[0]]);
          } else if (pool[1]) {
            picks.push(pool[1]);
          } else {
            // A force narrowed the pool to one: still surface the next-best offered
            // product so "show two" reliably returns two distinct matches.
            var ranked = offered.slice().sort(function (a, b) { return (r.totals[b] || 0) - (r.totals[a] || 0) || order[a] - order[b]; });
            for (var i = 0; i < ranked.length; i++) { if (ranked[i] !== picks[0]) { picks.push(ranked[i]); break; } }
          }
        }
        if (!picks.length) picks.push(tb || fallback);
        return picks;
      }

      function reveal() {
        var picks = rank();
        var tiles = quiz.querySelectorAll("[data-result-handle]");
        var shown = 0;
        tiles.forEach(function (t) {
          var on = picks.indexOf(t.getAttribute("data-result-handle")) !== -1;
          t.style.display = on ? "block" : "none";
          if (on) shown++;
        });
        // Safety net: never reveal an empty result. Fall back to the height-based
        // flagship, then the configured default, then the first result tile.
        if (!shown && tiles.length) {
          var fbHandle = computeTiebreak() || fallback;
          var fb = quiz.querySelector('[data-result-handle="' + fbHandle + '"]') || tiles[0];
          if (fb) { fb.style.display = "block"; shown = 1; }
        }
        quiz.setAttribute("data-result-count", String(shown));
        steps.forEach(function (s) { s.style.display = "none"; });
        if (resultWrap) resultWrap.style.display = "block";
        if (fillEl) fillEl.style.width = "100%";
        if (countEl) countEl.textContent = total + " / " + total;

        // Instrumentation: expose result + answers on the capture form and tag
        // the customer; notify Klaviyo now (anonymous) and again on email submit.
        quizPicks = picks; quizDetails = answerDetails();
        var rInput = quiz.querySelector("[data-numi-quiz-result]");
        if (rInput) rInput.value = picks.join(",");
        var aInput = quiz.querySelector("[data-numi-quiz-answers]");
        if (aInput) aInput.value = JSON.stringify(quizDetails);
        var tInput = quiz.querySelector("[data-numi-quiz-tags]");
        if (tInput) {
          var base = quiz.getAttribute("data-tag-base") || "fit-finder";
          var tags = [base];
          picks.forEach(function (h) { if (h) tags.push(base + "-" + h); });
          tInput.value = tags.join(",");
        }
        if (window._learnq && window._learnq.push) {
          window._learnq.push(["track", "Completed Fit Finder", { result: picks, answers: quizDetails }]);
        }
      }

      steps.forEach(function (step) {
        step.querySelectorAll("[data-opt-id]").forEach(function (opt) {
          opt.addEventListener("click", function () {
            answers[step.getAttribute("data-quiz-step-id")] = opt;
            step.querySelectorAll("[data-opt-id]").forEach(function (o) { o.classList.remove("is-selected"); });
            opt.classList.add("is-selected");
            setTimeout(function () {
              if (idx >= total - 1) reveal(); else { idx += 1; show(idx); }
            }, 200);
          });
        });
      });
      if (backBtn) backBtn.addEventListener("click", function () { if (idx > 0) { idx -= 1; show(idx); } });
      // Retake
      var retake = quiz.querySelector("[data-quiz-retake]");
      if (retake) retake.addEventListener("click", function (e) {
        e.preventDefault(); idx = 0; answers = {}; show(0);
        quiz.querySelectorAll("[data-opt-id]").forEach(function (o) { o.classList.remove("is-selected"); });
      });

      // On email submit, identify the profile in Klaviyo with the result +
      // answers (fires alongside the normal form submit, which tags the customer).
      var capture = quiz.querySelector("[data-numi-quiz-form]");
      if (capture) capture.addEventListener("submit", function () {
        var em = capture.querySelector('input[type="email"]');
        var email = em ? em.value : "";
        if (email && window._learnq && window._learnq.push) {
          window._learnq.push(["identify", { "$email": email, "FitFinderResult": quizPicks.join(","), "FitFinderAnswers": quizDetails }]);
          window._learnq.push(["track", "Fit Finder Signup", { result: quizPicks }]);
        }
      });

      show(0);
    });
  }

  /* ---------- Product card add-to-cart ----------
     Wires [data-numi-card-add] buttons inside numi product cards.  Single-variant
     products add directly via AJAX, refresh the cart drawer, and show a brief
     "Added ✓" confirmation.  Multi-variant products rely on the theme's own
     <quick-view> element (already on the page from app.js) — no JS needed.
     NOTE: the attribute is data-numi-card-add (NOT data-numi-add). The PDP buy
     box's submit button also carries data-numi-add, and this carousel shares the
     page with it on landing templates; matching that marker here would call
     preventDefault() on the buy box's submit and break its add-to-cart.   */
  function initProductCardAdd(scope) {
    scope.querySelectorAll("[data-numi-card-add]").forEach(function (btn) {
      if (!once(btn)) return;
      btn.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (btn.disabled) return;
        var variantId = btn.getAttribute("data-numi-card-add");
        if (!variantId) return;
        btn.disabled = true;
        var orig = btn.textContent;
        btn.textContent = "Adding...";

        fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Accept": "application/json" },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        }).then(function (r) { return r.json(); }).then(function () {
          document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
          var toggle = document.getElementById("cart-drawer-toggle");
          if (toggle) { toggle.click(); }
          else { window.location.href = "/cart"; }
          btn.textContent = "Added \u2713";
          btn.classList.add("numi-test-find__addbtn--done");
          setTimeout(function () {
            btn.textContent = orig;
            btn.classList.remove("numi-test-find__addbtn--done");
            btn.disabled = false;
          }, 2000);
        }).catch(function () {
          btn.disabled = false;
          btn.textContent = orig;
        });
      });
    });
  }

  function initAll(scope) {
    var root = scope || document;
    initGallery(root);
    initAccordion(root);
    initCarousels(root);
    initCollectionUI(root);
    initVideo(root);
    initSavings(root);
    initBuybox(root);
    initQuiz(root);
    initProductCardAdd(root);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { initAll(document); });
  } else {
    initAll(document);
  }
  document.addEventListener("shopify:section:load", function (e) { initAll(e.target || document); });
})();